// ============================================
// pages/unloading/UnloadingBillForm.tsx — Create/Edit Unloading Bill
// Route: /unloading/bills/new  OR  /unloading/bills/:id
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, Send, AlertCircle, CheckCircle, Package, XCircle, ArrowLeft, Loader2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  seasonsAPI, districtsAPI, commoditiesAPI, godownsAPI, unloadingContractorsAPI,
  unloadingBillsAPI, unloadingBillPaymentsAPI, unloadingEntriesAPI,
} from '../../api/services';
import { Layers, Truck } from 'lucide-react';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';
import { ServicePaymentsSection } from '../../components/ServicePaymentsSection';
import { InvoiceUploadCard } from '../../components/InvoiceUploadCard';

// ─── Status Badge ────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  draft:               { label: 'Draft',            bg: 'bg-yellow-50',  text: 'text-yellow-700', border: 'border-yellow-300' },
  warehouse_certified: { label: 'WH Certified',    bg: 'bg-blue-50',    text: 'text-blue-700',   border: 'border-blue-300' },
  dm_verified:         { label: 'DM Verified',      bg: 'bg-orange-50',  text: 'text-orange-700', border: 'border-orange-300' },
  manager_approved:    { label: 'Manager Approved', bg: 'bg-green-50',   text: 'text-green-700',  border: 'border-green-300' },
  rejected:            { label: 'Rejected',         bg: 'bg-red-50',     text: 'text-red-700',    border: 'border-red-300' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
};

interface BillForm {
  commodity_id: number;
  district_id: number;
  godown_id: number;
  godown_type: string;
  agreement_date: string;
  contractor_id: number;
  invoice_no: string;
  bill_date: string;
  billing_period_start: string;
  billing_period_end: string;
  num_bags_claimed: number;
  rate_per_bag: number;
  qty_mts: number;
  asor_charges: number;
  warehouse_certified: boolean;
  approved_80pct: boolean;
  difference_remarks: string;
  // DM verification
  dm_verified_bags: number;
  dm_verified_rate: number;
  dm_net_amount: number;
  dm_total: number;
  // Manager verification
  mgr_verified_bags: number;
  mgr_verified_rate: number;
  mgr_net_amount: number;
  mgr_total: number;
  // Payment
  tds_rate: number;
  payment_released: number;
}

const emptyForm: BillForm = {
  commodity_id: 0,
  district_id: 0,
  godown_id: 0,
  godown_type: '',
  agreement_date: '',
  contractor_id: 0,
  invoice_no: '',
  bill_date: '',
  billing_period_start: '',
  billing_period_end: '',
  num_bags_claimed: 0,
  rate_per_bag: 0,
  qty_mts: 0,
  asor_charges: 0,
  warehouse_certified: false,
  approved_80pct: false,
  difference_remarks: '',
  dm_verified_bags: 0,
  dm_verified_rate: 0,
  dm_net_amount: 0,
  dm_total: 0,
  mgr_verified_bags: 0,
  mgr_verified_rate: 0,
  mgr_net_amount: 0,
  mgr_total: 0,
  tds_rate: 2.0,
  payment_released: 0,
};

export const UnloadingBillForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const isCreateMode = !idParam || idParam === 'new';
  const recordId = isCreateMode ? null : parseInt(idParam);

  const [invoiceMeta, setInvoiceMeta] = useState<any>({});

  const isDM = hasRole(UserRole.DM);
  const isManager = hasRole(UserRole.AO_CAO, UserRole.MD);
  const isAdmin = hasRole(UserRole.SUPER_ADMIN);

  // Reference data
  const [season, setSeason] = useState<Season | null>(null);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);

  // Form state
  const [form, setForm] = useState<BillForm>(emptyForm);
  const [status, setStatus] = useState<string>('draft');
  const [rejectionReason, setRejectionReason] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isLocked = status === 'manager_approved' || status === 'warehouse_certified' || status === 'dm_verified';
  const canEditBase = isCreateMode || status === 'draft' || status === 'rejected';

  // ─── Entries picker state ─────────────────────────
  const [linkableEntries, setLinkableEntries] = useState<any[]>([]);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);

  const selectedEntryTotals = useMemo(() => {
    const ticked = linkableEntries.filter((e: any) => selectedEntryIds.has(e.id));
    return {
      total_entries: ticked.length,
      total_bags: ticked.reduce((s, e: any) => s + num(e.bags_unloaded), 0),
      total_weight_qtl: +ticked.reduce((s, e: any) => s + num(e.weight_qtl), 0).toFixed(3),
      total_amount_claimed: +ticked.reduce((s, e: any) => s + num(e.amount_claimed), 0).toFixed(2),
      total_amount_verified: +ticked.reduce((s, e: any) => s + num(e.amount_verified), 0).toFixed(2),
    };
  }, [linkableEntries, selectedEntryIds]);

  // ─── Auto-calculated fields ───────────────────────
  const billAmount = useMemo(() => form.num_bags_claimed * form.rate_per_bag, [form.num_bags_claimed, form.rate_per_bag]);
  const totalBillClaimed = useMemo(() => billAmount + form.asor_charges, [billAmount, form.asor_charges]);
  // 80% advance amount (per proformas: initial release is 80% of total)
  const approved80Amount = useMemo(() => form.approved_80pct ? Math.round(totalBillClaimed * 0.80) : totalBillClaimed, [totalBillClaimed, form.approved_80pct]);
  // TDS: Sec 194C — 2% for companies/firms, 1% for individuals (applied on full bill)
  const tdsAmount = useMemo(() => Math.round(totalBillClaimed * form.tds_rate / 100), [totalBillClaimed, form.tds_rate]);
  // Net payable = 80% advance minus TDS
  const netPayable = useMemo(() => approved80Amount - tdsAmount, [approved80Amount, tdsAmount]);
  // Balance remaining after payments released
  const balanceToRelease = useMemo(() => netPayable - form.payment_released, [netPayable, form.payment_released]);

  // ─── Load initial data ────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [seasonRes, distRes, commRes, contractorRes] = await Promise.all([
          seasonsAPI.active(),
          districtsAPI.list(),
          commoditiesAPI.list(),
          unloadingContractorsAPI.list(),
        ]);
        setSeason(seasonRes.data);
        setDistricts(distRes.data);
        setCommodities(commRes.data);
        setContractors(contractorRes.data);

        // Pre-select district for DM
        if (isCreateMode && user?.role === UserRole.DM && user.district_id) {
          setForm((prev) => ({ ...prev, district_id: user.district_id! }));
        }

        // Load existing record for edit mode
        if (!isCreateMode && recordId && seasonRes.data) {
          try {
            const billRes = await unloadingBillsAPI.get(seasonRes.data.id, recordId);
            const raw = billRes.data;
            setInvoiceMeta({
              invoice_file_name: raw.invoice_file_name || null,
              invoice_file_path: raw.invoice_file_path || null,
              invoice_size_bytes: raw.invoice_size_bytes || null,
              invoice_uploaded_at: raw.invoice_uploaded_at || null,
            });
            setForm({
              commodity_id: num(raw.commodity_id),
              district_id: num(raw.district_id),
              godown_id: num(raw.godown_id),
              godown_type: raw.godown_type || '',
              agreement_date: raw.agreement_date ? raw.agreement_date.split('T')[0] : '',
              contractor_id: num(raw.contractor_id),
              invoice_no: raw.invoice_no || '',
              bill_date: raw.bill_date ? raw.bill_date.split('T')[0] : '',
              billing_period_start: raw.billing_period_start ? String(raw.billing_period_start).slice(0, 10) : '',
              billing_period_end: raw.billing_period_end ? String(raw.billing_period_end).slice(0, 10) : '',
              num_bags_claimed: num(raw.num_bags_claimed),
              rate_per_bag: num(raw.rate_per_bag),
              qty_mts: num(raw.qty_mts),
              asor_charges: num(raw.asor_charges),
              warehouse_certified: !!raw.warehouse_certified,
              approved_80pct: !!raw.approved_80pct,
              difference_remarks: raw.difference_remarks || '',
              dm_verified_bags: num(raw.dm_verified_bags),
              dm_verified_rate: num(raw.dm_verified_rate),
              dm_net_amount: num(raw.dm_net_amount),
              dm_total: num(raw.dm_total),
              mgr_verified_bags: num(raw.mgr_verified_bags),
              mgr_verified_rate: num(raw.mgr_verified_rate),
              mgr_net_amount: num(raw.mgr_net_amount),
              mgr_total: num(raw.mgr_total),
              tds_rate: num(raw.tds_rate) || 2.0,
              payment_released: num(raw.payment_released),
            });
            setStatus(raw.status || 'draft');
            setRejectionReason(raw.rejection_reason || '');
          } catch (err: any) {
            setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load bill' });
          }
        }
      } catch {
        setMessage({ type: 'error', text: 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isCreateMode, recordId]);

  // ─── Load linkable entries ─────────────────────────
  const loadLinkableEntries = async () => {
    if (!season || !form.commodity_id || !form.district_id || !form.contractor_id) {
      setLinkableEntries([]);
      return;
    }
    setEntriesLoading(true);
    try {
      const params: any = {
        commodity_id: form.commodity_id,
        district_id: form.district_id,
        contractor_id: form.contractor_id,
      };
      if (recordId) params.bill_id = recordId;
      if (form.billing_period_start) params.period_start = form.billing_period_start;
      if (form.billing_period_end) params.period_end = form.billing_period_end;
      const res = await unloadingEntriesAPI.listLinkable(season.id, params);
      const list = Array.isArray(res.data) ? res.data : [];
      setLinkableEntries(list);
      if (recordId) {
        setSelectedEntryIds(new Set(list.filter((e: any) => e.unloading_bill_id === recordId).map((e: any) => e.id)));
      }
    } catch { setLinkableEntries([]); }
    finally { setEntriesLoading(false); }
  };

  useEffect(() => {
    loadLinkableEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season?.id, form.commodity_id, form.district_id, form.contractor_id, form.billing_period_start, form.billing_period_end, recordId]);

  const toggleEntry = (id: number) => {
    setSelectedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleLinkAndAggregate = async () => {
    if (!season || isCreateMode || !recordId) return;
    setLinkSaving(true);
    setMessage(null);
    try {
      await unloadingBillsAPI.linkEntries(season.id, recordId, Array.from(selectedEntryIds));
      setMessage({ type: 'success', text: `Linked ${selectedEntryIds.size} entry/entries and aggregated totals` });
      await loadLinkableEntries();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to link entries' });
    } finally {
      setLinkSaving(false);
    }
  };

  // ─── Load godowns when district changes ───────────
  useEffect(() => {
    if (!form.district_id) { setGodowns([]); return; }
    const loadGodowns = async () => {
      try {
        const res = await godownsAPI.list(form.district_id);
        setGodowns(res.data);
      } catch { setGodowns([]); }
    };
    loadGodowns();
  }, [form.district_id]);

  // ─── Auto-set TDS rate when contractor changes ────
  useEffect(() => {
    if (!form.contractor_id || !isCreateMode) return;
    const c = contractors.find((x: any) => x.id === form.contractor_id);
    if (!c) return;
    // Sec 194C: 2% for companies/firms/partnerships, 1% for individuals/proprietors
    const regType = (c.registration_type || '').toLowerCase();
    const rate = (regType.includes('individual') || regType.includes('proprietor')) ? 1.0 : 2.0;
    setForm((prev) => ({ ...prev, tds_rate: rate }));
  }, [form.contractor_id, contractors, isCreateMode]);

  // ─── Handlers ─────────────────────────────────────
  const handleChange = (field: keyof BillForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (submit: boolean = false) => {
    if (!season) return;

    if (!form.commodity_id || !form.district_id || !form.godown_id || !form.contractor_id) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    if (submit && form.num_bags_claimed <= 0) {
      setMessage({ type: 'error', text: 'Number of bags must be greater than 0' });
      return;
    }

    if (submit && !window.confirm('Submit this bill?')) return;

    setSaving(true);
    setMessage(null);

    try {
      const payload = {
        commodity_id: form.commodity_id,
        district_id: form.district_id,
        godown_id: form.godown_id,
        godown_type: form.godown_type,
        agreement_date: form.agreement_date,
        contractor_id: form.contractor_id,
        invoice_no: form.invoice_no,
        bill_date: form.bill_date,
        billing_period_start: form.billing_period_start || null,
        billing_period_end: form.billing_period_end || null,
        num_bags_claimed: form.num_bags_claimed,
        rate_per_bag: form.rate_per_bag,
        qty_mts: form.qty_mts,
        asor_charges: form.asor_charges,
        warehouse_certified: form.warehouse_certified,
        approved_80pct: form.approved_80pct,
        difference_remarks: form.difference_remarks,
        bill_amount: billAmount,
        total_bill_claimed: totalBillClaimed,
        approved_80pct_amount: approved80Amount,
        tds_rate: form.tds_rate,
        tds_amount: tdsAmount,
        net_payable: netPayable,
        payment_released: form.payment_released,
        balance_to_release: balanceToRelease,
      };

      if (isCreateMode) {
        const res = await unloadingBillsAPI.create(season.id, payload);
        const newId = res.data.id;
        setMessage({ type: 'success', text: submit ? 'Bill created and submitted' : 'Bill created as draft' });
        if (newId) navigate(`/unloading/bills/${newId}`, { replace: true });
      } else if (recordId) {
        await unloadingBillsAPI.update(season.id, recordId, payload);
        setMessage({ type: 'success', text: submit ? 'Bill submitted' : 'Draft saved' });
        if (!submit) setTimeout(() => setMessage(null), 3000);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back */}
      <button onClick={() => navigate('/unloading/bills')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Bills
      </button>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="w-7 h-7 text-blue-600" />
            {isCreateMode ? 'New Unloading Bill' : 'Unloading Bill'}
          </h1>
          {!isCreateMode && <StatusBadge status={status} />}
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {isCreateMode ? 'Create a new unloading bill' : `Invoice: ${form.invoice_no || '--'}`}
          {season && <span className="text-blue-600 font-medium"> | {season.season_name}</span>}
        </p>
      </div>

      {/* Rejection banner */}
      {status === 'rejected' && rejectionReason && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div><p className="font-semibold">Rejected</p><p className="mt-0.5">Reason: {rejectionReason}</p></div>
        </div>
      )}

      {isLocked && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
          <AlertCircle className="w-4 h-4" />
          This bill has been {status.replace('_', ' ')}. Base fields are read-only.
        </div>
      )}

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* ─── Basic Info Section ─────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Bill Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Commodity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commodity <span className="text-red-500">*</span></label>
            <select value={form.commodity_id} onChange={(e) => handleChange('commodity_id', parseInt(e.target.value))}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {commodities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* District */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">District <span className="text-red-500">*</span></label>
            {isDM && !isAdmin ? (
              <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm font-medium text-green-700">
                {districts.find((d) => d.id === form.district_id)?.name || `District #${form.district_id}`}
              </div>
            ) : (
              <select value={form.district_id} onChange={(e) => handleChange('district_id', parseInt(e.target.value))}
                disabled={!canEditBase}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
                <option value={0}>-- Select --</option>
                {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>

          {/* Godown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Godown <span className="text-red-500">*</span></label>
            <select value={form.godown_id} onChange={(e) => handleChange('godown_id', parseInt(e.target.value))}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {godowns.map((g) => <option key={g.id} value={g.id}>{g.godown_name || g.name}</option>)}
            </select>
            {godowns.length === 0 && form.district_id > 0 && (
              <p className="text-xs text-amber-600 mt-1">No godowns found for this district</p>
            )}
          </div>

          {/* Godown Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Godown Type</label>
            <select value={form.godown_type} onChange={(e) => handleChange('godown_type', e.target.value)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
              <option value="">-- Select --</option>
              <option value="CWC">CWC</option>
              <option value="TGWC">TGWC</option>
              <option value="AMC">AMC</option>
              <option value="IG">IG (Hire)</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          {/* H&T Agreement Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">H&amp;T Agreement Date</label>
            <input type="date" value={form.agreement_date} onChange={(e) => handleChange('agreement_date', e.target.value)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>

          {/* Contractor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contractor <span className="text-red-500">*</span></label>
            <select value={form.contractor_id} onChange={(e) => handleChange('contractor_id', parseInt(e.target.value))}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.firm_name || c.name}</option>)}
            </select>
          </div>

          {/* Invoice No */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No</label>
            <input type="text" value={form.invoice_no} onChange={(e) => handleChange('invoice_no', e.target.value)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>

          {/* Bill Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bill Date</label>
            <input type="date" value={form.bill_date} onChange={(e) => handleChange('bill_date', e.target.value)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>

          {/* Billing Period — scopes which unloading entries aggregate */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Billing Period <span className="text-xs text-gray-400 font-normal">(from invoice — scopes which entries appear below)</span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={form.billing_period_start} onChange={(e) => handleChange('billing_period_start', e.target.value)}
                disabled={!canEditBase} className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" value={form.billing_period_end} onChange={(e) => handleChange('billing_period_end', e.target.value)}
                disabled={!canEditBase} className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
              {canEditBase && (
                <>
                  <button type="button" onClick={() => {
                    const d = new Date(); const day = d.getDay(); const mon = new Date(d); mon.setDate(d.getDate() - ((day + 6) % 7));
                    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
                    setForm((f: any) => ({ ...f, billing_period_start: mon.toISOString().slice(0, 10), billing_period_end: sun.toISOString().slice(0, 10) }));
                  }} className="px-2 py-1 text-xs text-blue-700 border border-blue-200 hover:bg-blue-50 rounded">This Week</button>
                  <button type="button" onClick={() => {
                    const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth(), 1); const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                    setForm((f: any) => ({ ...f, billing_period_start: s.toISOString().slice(0, 10), billing_period_end: e.toISOString().slice(0, 10) }));
                  }} className="px-2 py-1 text-xs text-blue-700 border border-blue-200 hover:bg-blue-50 rounded">This Month</button>
                  <button type="button" onClick={() => {
                    const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth() - 1, 1); const e = new Date(d.getFullYear(), d.getMonth(), 0);
                    setForm((f: any) => ({ ...f, billing_period_start: s.toISOString().slice(0, 10), billing_period_end: e.toISOString().slice(0, 10) }));
                  }} className="px-2 py-1 text-xs text-blue-700 border border-blue-200 hover:bg-blue-50 rounded">Last Month</button>
                  <button type="button" onClick={() => setForm((f: any) => ({ ...f, billing_period_start: '', billing_period_end: '' }))}
                    className="px-2 py-1 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 rounded">Clear</button>
                </>
              )}
            </div>
          </div>

          {/* Bags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Bags Claimed</label>
            <input type="number" value={form.num_bags_claimed || ''} min={0}
              onChange={(e) => handleChange('num_bags_claimed', parseInt(e.target.value) || 0)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>

          {/* Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rate per Bag (Rs.)</label>
            <input type="number" step="0.01" value={form.rate_per_bag || ''} min={0}
              onChange={(e) => handleChange('rate_per_bag', parseFloat(e.target.value) || 0)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>

          {/* Qty MTs */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (MTs)</label>
            <input type="number" step="0.001" value={form.qty_mts || ''} min={0}
              onChange={(e) => handleChange('qty_mts', parseFloat(e.target.value) || 0)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>

          {/* ASOR Charges */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ASOR Charges (Rs.)</label>
            <input type="number" step="0.01" value={form.asor_charges || ''} min={0}
              onChange={(e) => handleChange('asor_charges', parseFloat(e.target.value) || 0)}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>

          {/* Warehouse certified flag */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.warehouse_certified}
              onChange={(e) => handleChange('warehouse_certified', e.target.checked)}
              disabled={!canEditBase}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50" />
            <span className="text-sm text-gray-700">Certified by Warehouse Manager</span>
          </label>

          {/* 80% release flag */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.approved_80pct}
              onChange={(e) => handleChange('approved_80pct', e.target.checked)}
              disabled={!(isDM || isAdmin) || !['warehouse_certified'].includes(status)}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 disabled:opacity-50" />
            <span className="text-sm text-gray-700">File approved @80%</span>
          </label>

          {/* Difference remarks (spans both columns) */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks on Difference</label>
            <textarea value={form.difference_remarks} rows={2}
              onChange={(e) => handleChange('difference_remarks', e.target.value)}
              disabled={!(isDM || isAdmin) || !['warehouse_certified', 'dm_verified'].includes(status)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50" />
          </div>
        </div>

        {/* Auto-calc bar */}
        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Auto-Calculated Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500">Bill Amount</p>
              <p className="text-base font-bold text-gray-800">{formatAmount(billAmount)}</p>
              <p className="text-[10px] text-gray-400">Bags x Rate</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Claimed</p>
              <p className="text-base font-bold text-blue-600">{formatAmount(totalBillClaimed)}</p>
              <p className="text-[10px] text-gray-400">Bill + ASOR</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{form.approved_80pct ? 'Approved @80%' : 'Approved (Full)'}</p>
              <p className="text-base font-bold text-purple-600">{formatAmount(approved80Amount)}</p>
              <p className="text-[10px] text-gray-400">{form.approved_80pct ? 'Total x 0.80' : '100% of Total'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">TDS @{form.tds_rate}%</p>
              <p className="text-base font-bold text-red-600">-{formatAmount(tdsAmount)}</p>
              <p className="text-[10px] text-gray-400">Sec 194C on Total</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Net Payable</p>
              <p className="text-base font-bold text-green-700">{formatAmount(netPayable)}</p>
              <p className="text-[10px] text-gray-400">Approved - TDS</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Balance</p>
              <p className={`text-base font-bold ${balanceToRelease < 0 ? 'text-red-600' : 'text-orange-600'}`}>
                {formatAmount(balanceToRelease)}
              </p>
              <p className="text-[10px] text-gray-400">Net - Released</p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── DM Verification Section ─────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">DM Verification</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DM Verified Bags</label>
            <input type="number" value={form.dm_verified_bags || ''} min={0}
              onChange={(e) => handleChange('dm_verified_bags', parseInt(e.target.value) || 0)}
              disabled={!(isDM || isAdmin) || !['warehouse_certified'].includes(status)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DM Verified Rate</label>
            <input type="number" step="0.01" value={form.dm_verified_rate || ''} min={0}
              onChange={(e) => handleChange('dm_verified_rate', parseFloat(e.target.value) || 0)}
              disabled={!(isDM || isAdmin) || !['warehouse_certified'].includes(status)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DM Net Amount</label>
            <input type="number" value={form.dm_net_amount || ''} readOnly
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DM Total</label>
            <input type="number" value={form.dm_total || ''} readOnly
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
      </div>

      {/* ─── Manager Verification Section ────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Manager Verification</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager Verified Bags</label>
            <input type="number" value={form.mgr_verified_bags || ''} min={0}
              onChange={(e) => handleChange('mgr_verified_bags', parseInt(e.target.value) || 0)}
              disabled={!(isManager || isAdmin) || !['dm_verified'].includes(status)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager Verified Rate</label>
            <input type="number" step="0.01" value={form.mgr_verified_rate || ''} min={0}
              onChange={(e) => handleChange('mgr_verified_rate', parseFloat(e.target.value) || 0)}
              disabled={!(isManager || isAdmin) || !['dm_verified'].includes(status)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager Net Amount</label>
            <input type="number" value={form.mgr_net_amount || ''} readOnly
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager Total</label>
            <input type="number" value={form.mgr_total || ''} readOnly
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
      </div>

      {/* ─── TDS & Payment Section ────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">TDS & Payment</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">TDS Rate (%) <span className="text-[10px] text-gray-400">Sec 194C</span></label>
            <select value={form.tds_rate} onChange={(e) => handleChange('tds_rate', parseFloat(e.target.value))}
              disabled={!canEditBase}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50">
              <option value={1.0}>1% — Individual / Proprietor / HUF</option>
              <option value={2.0}>2% — Company / Firm / Partnership</option>
            </select>
            {form.contractor_id > 0 && (
              <p className="text-[10px] text-gray-400 mt-1">
                Auto-set from contractor type. Override if needed.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">TDS Deducted (Rs.)</label>
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-bold text-red-700">
              {formatAmount(tdsAmount)}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Total Claimed x {form.tds_rate}%</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Released (Rs.) <span className="text-[10px] text-gray-400 font-normal">(from installments)</span>
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700">
              {formatAmount(form.payment_released || 0)}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              {isCreateMode ? 'Enter installments after saving' : 'Auto-synced from Payment Installments below'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Balance to Release</label>
            <div className={`px-3 py-2 rounded-lg border text-sm font-bold ${
              balanceToRelease <= 0 ? 'bg-gray-50 border-gray-200 text-gray-500' : 'bg-green-50 border-green-200 text-green-700'
            }`}>
              {balanceToRelease > 0 ? formatAmount(balanceToRelease) : balanceToRelease === 0 ? 'Fully Paid' : formatAmount(0)}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Net Payable - Released</p>
          </div>
        </div>
      </div>

      {/* ─── Actions ─────────────────────────────────── */}
      <div className="flex justify-end gap-3 mb-10">
        {canEditBase && (
          <>
            <button onClick={() => handleSave(false)} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isCreateMode ? 'Save as Draft' : 'Save Draft'}
            </button>
            <button onClick={() => handleSave(true)} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit
            </button>
          </>
        )}
      </div>

      {/* Payment Installments — only after the bill is saved */}
      {/* Unloading Entries Picker — visible as soon as scope is set */}
      {form.commodity_id && form.district_id && form.contractor_id && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Truck className="w-4 h-4 text-blue-600" />
              Approved Unloading Entries for this Bill
            </h3>
            {!isCreateMode && recordId && (
              <button
                onClick={handleLinkAndAggregate}
                disabled={linkSaving || isLocked}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
              >
                {linkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                Link &amp; Aggregate ({selectedEntryIds.size})
              </button>
            )}
            {isCreateMode && (
              <span className="text-xs text-gray-500 italic">
                {selectedEntryIds.size} selected — save draft first then link (or re-pick after save)
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Approved entries for this contractor
            {form.billing_period_start && form.billing_period_end
              ? <> between <span className="font-semibold">{form.billing_period_start}</span> and <span className="font-semibold">{form.billing_period_end}</span></>
              : <> (set a billing period above to scope by date)</>
            }. Tick the ones covered by the invoice.
          </p>

          {selectedEntryIds.size > 0 && (
            <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200 grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
              <div><p className="text-[10px] text-gray-500 uppercase">Entries</p><p className="text-sm font-bold text-gray-900">{selectedEntryTotals.total_entries}</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">Bags</p><p className="text-sm font-bold text-gray-900">{selectedEntryTotals.total_bags.toLocaleString('en-IN')}</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">Weight (Qtl)</p><p className="text-sm font-bold text-gray-900">{selectedEntryTotals.total_weight_qtl.toLocaleString('en-IN')}</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">Claimed</p><p className="text-sm font-bold text-blue-600">{formatAmount(selectedEntryTotals.total_amount_claimed)}</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">Verified</p><p className="text-sm font-bold text-green-700">{formatAmount(selectedEntryTotals.total_amount_verified)}</p></div>
            </div>
          )}

          {entriesLoading ? (
            <div className="py-6 text-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading entries...
            </div>
          ) : linkableEntries.length === 0 ? (
            <div className="py-6 text-center text-gray-400 text-sm">
              No approved entries match this bill's scope. Add entries under <span className="font-semibold">/unloading/entries</span> first.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Entry ID</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Date</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Godown</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Bags</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Wt (Qtl)</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Claimed</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Verified</th>
                    <th className="px-2 py-2 text-center font-semibold text-gray-600">Current Bill</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {linkableEntries.map((e: any) => {
                    const checked = selectedEntryIds.has(e.id);
                    const ownedByThis = e.unloading_bill_id === recordId;
                    return (
                      <tr key={e.id} className={checked ? 'bg-purple-50' : 'hover:bg-gray-50'}>
                        <td className="px-2 py-2">
                          <input type="checkbox" checked={checked} disabled={isLocked}
                            onChange={() => toggleEntry(e.id)}
                            className="rounded border-gray-300 text-purple-600" />
                        </td>
                        <td className="px-2 py-2 text-gray-700">{e.entry_id_ext || `#${e.id}`}</td>
                        <td className="px-2 py-2 text-gray-700">{e.entry_date || '—'}</td>
                        <td className="px-2 py-2 text-gray-700">{e.godown?.godown_name || '—'}</td>
                        <td className="px-2 py-2 text-right text-gray-700">{num(e.bags_unloaded).toLocaleString('en-IN')}</td>
                        <td className="px-2 py-2 text-right text-gray-700">{num(e.weight_qtl).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="px-2 py-2 text-right text-gray-700">{formatAmount(num(e.amount_claimed))}</td>
                        <td className="px-2 py-2 text-right text-gray-900 font-medium">{formatAmount(num(e.amount_verified))}</td>
                        <td className="px-2 py-2 text-center">
                          {ownedByThis
                            ? <span className="text-xs text-green-700 font-medium">This bill</span>
                            : <span className="text-xs text-gray-400">Unlinked</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!isCreateMode && recordId && (
        <ServicePaymentsSection
          parentId={recordId}
          api={unloadingBillPaymentsAPI}
          totalOwed={netPayable}
          canEdit={canEditBase}
          label="H&T contractor"
          onChange={(released) => {
            setForm((prev) =>
              prev.payment_released === released ? prev : { ...prev, payment_released: released },
            );
          }}
        />
      )}

      {!isCreateMode && recordId && season?.id && (
        <InvoiceUploadCard
          seasonId={season.id}
          recordId={recordId}
          api={unloadingBillsAPI}
          record={invoiceMeta}
          locked={!canEditBase}
          label="Contractor Invoice"
          description="Upload the unloading contractor's invoice (PDF, PNG, or JPG — max 10 MB)."
          onChange={(u) => setInvoiceMeta({
            invoice_file_name: u.invoice_file_name || null,
            invoice_file_path: u.invoice_file_path || null,
            invoice_size_bytes: u.invoice_size_bytes || null,
            invoice_uploaded_at: u.invoice_uploaded_at || null,
          })}
        />
      )}
    </div>
  );
};

export default UnloadingBillForm;
